import { useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useCategories } from './catalogCategories';
import { useCatalogItems } from './catalogItems';
import { paintedAs, surfaceKey, surfaceOf, useMaterialSurfaces } from './materialSurface';
import { partKindOf } from './modelsStages';
import { usePartCategories } from './partCategories';
import { partCatalogId } from './partIdentity';
import {
  materialsOfPart, partNamesOf, setPartMaterials, setPartNames, usePartMaterialNames, usePartMaterials,
  usePartMaterialsAlso, wantsPartMaterial, type PartNames,
} from './partMaterialRun';
import { usePartModelJobs } from './partModels';
import { usePartsRun } from './partsRun';
import { namedIn } from './partsCatalogue';
import { PopMenu } from './PopMenu';
import { colourJob } from './threeDApi';
import { materialText, type MaterialPair } from './variantLabel';
import { useLanguage } from '../../contexts/LanguageContext';

// WHAT EACH PART IS MADE OF, asked the other way round: per material, which parts. It was a table
// of its own — a row per part and a menu of materials in each — beside the materials table; it is
// that table's PARTS column now, so a material and what wears it are one row.
//
// ONE MATERIAL PER PART, BAR A TOOL. Ticking a part here moves it off whichever row had it;
// unticking it is back to never-asked, which is not the same as "none of these" (see
// clearPartMaterial). A tool is a blade and a handle, and stays on every row it is ticked on
// (`several`): its model is built with those names on its meshes, and each is painted by its own.

export type PickablePart = {
  at: number;
  label: string;
  // Hardware, or a tool: what the default materials are for, and what their call is asked about.
  hardware: boolean;
  // A component is the product, made of what the listing names, and is offered on those rows
  // alone; hardware on any (see materialOptions).
  component: boolean;
  // MAY BE ON MORE THAN ONE ROW: a tool, made of more than one thing.
  several: boolean;
};

// What each part is on, by index — every material of it, its first first.
export type Worn = Record<number, MaterialPair[]>;

// The parts, what each is made of in this colourway, and the two gestures. The repaint of a piece
// of hardware's kept mesh travels with the pick, as it did in the per-part table: a component
// follows through the product (see setPartMaterial), a screw is in no piece of it.
export function useMaterialParts(docId: string, submodel: string) {
  const { items } = usePartsRun(docId);
  const categories = usePartCategories(items);
  const ontology = useCategories();
  // Subscribed to both halves of what a part is on — see materialsOfPart.
  usePartMaterials(docId);
  usePartMaterialsAlso(docId);
  usePartMaterialNames(docId);
  const { items: catalog, loaded } = useCatalogItems();
  const { modelOf } = usePartModelJobs();
  // Subscribed, so a colour a material has just been given is the one a repaint uses.
  useMaterialSurfaces(docId);

  const parts: PickablePart[] = items
    .map((p, i) => ({ p, i, kind: partKindOf(p, categories[i], ontology) }))
    .map(({ p, i, kind }) => ({
      at: i,
      // THE NAME ALONE: the number and the letter are the parts table's way to point at a row,
      // and here the row is the material.
      label: namedIn(p, 'en') || `#${i}`,
      // A TOOL IS HARDWARE HERE: in no piece of the product, in the same bag as the screws, made
      // of what the listing does not name — and its kept mesh is repainted where it is picked.
      // They were left out, and a screwdriver built in the tools subphase stayed the grey it
      // was built in, with no row to put it on and no call asked what it is made of.
      hardware: kind === 'Hardware' || kind === 'Tools',
      component: wantsPartMaterial(p, categories[i]),
      several: kind === 'Tools',
    }));
  const worn: Worn = {};
  // AND WHICH PART OF A TOOL EACH OF ITS MATERIALS IS, by the material's key (see PartNames).
  const partOf: Record<number, PartNames> = {};
  for (const part of parts) {
    const on = materialsOfPart(docId, submodel, part.at);
    if (on.length) worn[part.at] = on;
    if (part.several) partOf[part.at] = partNamesOf(docId, submodel, part.at);
  }

  // EVERYTHING A PART IS ON, SET AT ONCE — one material, or a tool's several; none is back to
  // never-asked. Each is painted onto the kept mesh by its own name: a mesh of one material takes
  // it whole, a tool's takes each on the meshes that wear that name.
  //
  // `of` is which part of the tool each is, where the caller knows (a reading being applied);
  // without it the words already kept stay with the materials still ticked.
  const put = (part: PickablePart, said: MaterialPair[], of?: PartNames) => {
    // ONE, for anything that is not a tool — whatever an answer said of a screw.
    const pairs = part.several ? said : said.slice(0, 1);
    const names = of ?? partNamesOf(docId, submodel, part.at);
    setPartMaterials(docId, submodel, part.at, pairs);
    setPartNames(docId, submodel, part.at, part.several ? names : {});
    if (!part.hardware) return;
    const itemId = partCatalogId(catalog, loaded, items[part.at]);
    const job = itemId == null ? null : modelOf.get(itemId);
    if (!job) return;
    void (async () => {
      // One after another: each paint rewrites the same file.
      for (const pair of pairs) {
        const surface = surfaceOf(docId, pair);
        // NOT PAINTED UNTIL IT IS JUDGED: a colour, a roughness and a metallic, all three.
        if (!surface.rgb || surface.roughness == null || surface.metallic == null) continue;
        await colourJob(job.id, surface.rgb, undefined, surface.fromUrl, paintedAs(pair, surface));
      }
    })().catch((e) => console.warn('[materials] could not repaint', items[part.at]?.name, e));
  };

  // WHICH PART OF THE TOOL ONE OF ITS MATERIALS IS, by hand. A word about the next build: nothing
  // is repainted, the kept mesh is made of what it is made of.
  const name = (part: PickablePart, pair: MaterialPair, text: string) =>
    setPartNames(docId, submodel, part.at, { ...partNamesOf(docId, submodel, part.at), [surfaceKey(pair)]: text });

  return { parts, worn, partOf, put, name };
}

// ONE ROW'S PARTS: the ones wearing it, and a menu of checkboxes to change that. The menu is a
// portal (PopMenu), so the table's sideways scroll does not cut it off.
export function MaterialPartsPicker({ pair, listed, parts, worn, partOf, put, name, disabled }: {
  pair: MaterialPair;
  // Whether this is one of the listing's materials — the only rows a component may be put on.
  listed: boolean;
  disabled?: boolean;
} & ReturnType<typeof useMaterialParts>) {
  const { t } = useLanguage();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const mine = surfaceKey(pair);
  const wears = (part: PickablePart) => (worn[part.at] ?? []).some((p) => surfaceKey(p) === mine);
  // A TICK: onto this row — beside the others for a tool, off them for anything else — or off it.
  const toggle = (part: PickablePart) => {
    const on = worn[part.at] ?? [];
    if (wears(part)) put(part, on.filter((p) => surfaceKey(p) !== mine));
    else put(part, part.several ? [...on, pair] : [pair]);
  };
  // THE COMPONENTS, THE HARDWARE, THEN THE TOOLS, each kept in the list's own order — three
  // kinds of thing, with a rule between them in the menu.
  const kindOf = (part: PickablePart) => (part.several ? 2 : part.hardware ? 1 : 0);
  const offered = parts.filter((part) => listed || !part.component)
    .sort((a, b) => kindOf(a) - kindOf(b));
  const on = offered.filter(wears);

  if (!offered.length) return <span className="text-slate-300">—</span>;
  return (
    <>
      <button
        ref={anchor}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={t('notice.3d.materialPartsPick')}
        className="inline-flex w-full items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-800 transition-colors hover:border-blue-500 disabled:opacity-50"
      >
        {/* ONE PART A LINE, centred, the names never wrapping — so the column is as wide as the
            longest of them, and every row's menu fills it: one width down the column. */}
        <span className="flex min-w-0 flex-1 flex-col items-center text-center">
          {on.length
            ? on.map((part) => (
              <span key={part.at} className="whitespace-nowrap">
                {part.label}
                {/* WHICH PART OF IT, for a tool on several rows: its blade here, its handle there. */}
                {partOf[part.at]?.[mine] && ` (${partOf[part.at][mine]})`}
              </span>
            ))
            : <span className="italic text-slate-400">{t('notice.3d.materialOnNothing')}</span>}
        </span>
        <ChevronDown className="h-3 w-3 shrink-0 text-slate-400" />
      </button>
      {open && (
        <PopMenu anchor={anchor} onClose={() => setOpen(false)}>
          <div className="max-h-80 overflow-y-auto py-1">
            {offered.map((part, i) => {
              const checked = wears(part);
              const ruled = i > 0 && kindOf(offered[i - 1]) !== kindOf(part);
              // WHERE IT IS NOW, when that is another row: ticking it here takes it from there.
              // (Or beside it, for a tool: what else it is made of.)
              const elsewhere = checked ? '' : (worn[part.at] ?? []).map(materialText).join(' & ');
              return (
                <div key={part.at} className={`flex items-center hover:bg-slate-100 ${ruled ? 'mt-1 border-t border-slate-200 pt-1' : ''}`}>
                  <button
                    type="button"
                    onClick={() => toggle(part)}
                    className="flex min-w-0 flex-1 items-center justify-center gap-2 px-3 py-1.5 text-center text-xs text-slate-700"
                  >
                    <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                      checked ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-400'}`}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex flex-col items-center">
                      <span>{part.label}</span>
                      {elsewhere && <span className="text-[10px] text-slate-400">{elsewhere}</span>}
                    </span>
                  </button>
                  {/* WHICH PART OF THE TOOL THIS MATERIAL IS — the call's word, or a hand's. Kept
                      as it is left, not per key: it is read by the next build, not by the page. */}
                  {checked && part.several && (
                    <input
                      key={partOf[part.at]?.[mine] ?? ''}
                      defaultValue={partOf[part.at]?.[mine] ?? ''}
                      placeholder={t('notice.3d.materialPartOf')}
                      onBlur={(e) => {
                        if (e.target.value.trim() !== (partOf[part.at]?.[mine] ?? '')) name(part, pair, e.target.value);
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      className="mr-2 w-24 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[11px] text-slate-800 focus:border-blue-500 focus:outline-none"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </PopMenu>
      )}
    </>
  );
}
